import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from "react";
import { Box, IconButton } from "@mui/material";
import { FaStar, FaRegStar } from "react-icons/fa";
import { sendFeedback } from "../../../../axios/feedback";
import { toast } from "react-toastify";
import type { Message } from "../../../../types";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../../utils/internetConnection";
import { useAppStore } from "../../../../stores/appStore";

interface TabbedMessageStarRatingProps {
  message: Message;
  activeTabId?: string;
  activeTabLabel?: string;
  activeTabText?: string;
}

const STAR_COUNT = 5;

const getStorageKey = (messageId: string, tabId?: string) =>
  `tab_rating_${messageId}_${tabId ?? "default"}`;

const getScrollParent = (node: HTMLElement | null): HTMLElement | Document => {
  if (!node) return document;

  let parent = node.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return document;
};

export const TabbedMessageStarRating = ({
  message,
  activeTabId,
  activeTabLabel,
  activeTabText,
}: TabbedMessageStarRatingProps) => {
  const isOnline = useAppStore((s) => s.isOnline);
  const storageKey = getStorageKey(message.message_id, activeTabId);

  const [selectedStars, setSelectedStars] = useState<number>(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? Number(saved) : 0;
  });
  const [hoveredStars, setHoveredStars] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPointerInsideRef = useRef(false);
  const handledViaPointerRef = useRef(false);
  const isScrollingRef = useRef(false);
  const lastPointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const scrollEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    setSelectedStars(saved ? Number(saved) : 0);
    setHoveredStars(null);
    isPointerInsideRef.current = false;
  }, [storageKey]);

  const clearHover = useCallback(() => {
    if (selectedStars) return;
    isPointerInsideRef.current = false;
    setHoveredStars(null);
  }, [selectedStars]);

  const syncHoverFromPointer = useCallback(() => {
    if (selectedStars || isScrollingRef.current) return;

    const container = containerRef.current;
    const pos = lastPointerPositionRef.current;
    if (!container || !pos) {
      clearHover();
      return;
    }

    const target = document.elementFromPoint(pos.x, pos.y);
    if (!target || !container.contains(target)) {
      clearHover();
      return;
    }

    const starButton = target.closest("[data-star-value]");
    if (!starButton || !container.contains(starButton)) {
      clearHover();
      return;
    }

    const value = Number(starButton.getAttribute("data-star-value"));
    if (!Number.isFinite(value) || value < 1 || value > STAR_COUNT) {
      clearHover();
      return;
    }

    isPointerInsideRef.current = true;
    setHoveredStars(value);
  }, [clearHover, selectedStars]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollParent = getScrollParent(container);

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      lastPointerPositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
    };

    const handleScroll = () => {
      isScrollingRef.current = true;
      clearHover();

      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }

      scrollEndTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
        syncHoverFromPointer();
      }, 100);
    };

    document.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    scrollParent.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      scrollParent.removeEventListener("scroll", handleScroll, {
        capture: true,
      });

      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
    };
  }, [clearHover, syncHoverFromPointer]);

  useEffect(() => {
    if (hoveredStars == null || selectedStars) return;

    const handleGlobalPointerMove = (event: globalThis.PointerEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const isInside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!isInside) {
        clearHover();
      }
    };

    document.addEventListener("pointermove", handleGlobalPointerMove);
    return () =>
      document.removeEventListener("pointermove", handleGlobalPointerMove);
  }, [clearHover, hoveredStars, selectedStars]);

  const displayStars = hoveredStars ?? selectedStars;

  const handleContainerPointerEnter = () => {
    isPointerInsideRef.current = true;
  };

  const handleContainerPointerLeave = (
    event: PointerEvent<HTMLDivElement>,
  ) => {
    const relatedTarget = event.relatedTarget;
    if (
      relatedTarget instanceof Node &&
      containerRef.current?.contains(relatedTarget)
    ) {
      return;
    }
    clearHover();
  };

  const handleStarPointerEnter = (
    value: number,
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (
      event.pointerType !== "mouse" ||
      selectedStars ||
      isScrollingRef.current
    ) {
      return;
    }

    isPointerInsideRef.current = true;
    setHoveredStars(value);
  };

  const handleStarPointerDown = (
    value: number,
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType !== "mouse") {
      event.preventDefault();
      handledViaPointerRef.current = true;
      handleStarClick(value);
    }
  };

  const handleStarClick = async (
    value: number,
    event?: MouseEvent<HTMLButtonElement>,
  ) => {
    if (event && handledViaPointerRef.current) {
      handledViaPointerRef.current = false;
      return;
    }

    const hasFinePointer =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: fine)").matches;

    if (
      hasFinePointer &&
      event &&
      event.detail !== 0 &&
      !isPointerInsideRef.current
    ) {
      return;
    }

    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    if (selectedStars || isSubmitting) return;

    setSelectedStars(value);
    setHoveredStars(null);
    setIsSubmitting(true);

    try {
      await sendFeedback({
        selected_stars: value,
        feedback_type: "message",
        message_id: message.message_id,
        message_text: activeTabText || "",
        message_type: "tabbed_message",
        reason_label: activeTabLabel || undefined,
        domain: message.domain || "Unknown",
        focus: message.focus || "Unknown",
        user_id: message.user_id,
        date: message.date,
      });

      localStorage.setItem(storageKey, String(value));

      toast.success("Thank you for your rating!", {
        position: "top-right",
        autoClose: 2000,
        closeOnClick: false,
        pauseOnHover: true,
      });
    } catch (error) {
      console.error("Star rating submission error:", error);
      setSelectedStars(0);
      localStorage.removeItem(storageKey);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0,
      }}
      onPointerEnter={handleContainerPointerEnter}
      onPointerLeave={handleContainerPointerLeave}
      onPointerCancel={clearHover}
    >
      {Array.from({ length: STAR_COUNT }, (_, index) => {
        const value = index + 1;
        const isActive = value <= displayStars;

        return (
          <IconButton
            key={value}
            size="small"
            disableRipple
            data-star-value={value}
            disabled={!!selectedStars || isSubmitting}
            onPointerEnter={(event) => handleStarPointerEnter(value, event)}
            onPointerDown={(event) => handleStarPointerDown(value, event)}
            onClick={(event) => handleStarClick(value, event)}
            sx={{
              p: "2px",
              color: isActive ? "#F5C518" : "#9E9E9E",
              transition: "color 0.15s",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-disabled": {
                color: isActive ? "#F5C518" : "#9E9E9E",
              },
            }}
          >
            {isActive ? <FaStar size={22} /> : <FaRegStar size={22} />}
          </IconButton>
        );
      })}
    </Box>
  );
};
